import { useState, useEffect } from 'react';
import { useParams, useRouter, useSearchParams } from '@/lib/react-router-compat';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui-kit/button';
import { Badge } from '@/components/ui-kit/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';
import { FileText, Code2, Play } from 'lucide-react';
import { useCreateApiContext } from '../create-api-context';
import { useCreateApiAction, useTestApiAction } from '@/modules/gpt-chats/hooks/use-tools';
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast';

import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { IParameter } from '@/modules/gpt-chats/types/tools.service.type';
import { DebugParameterList } from './components/debug-parameter-components';
import { generateInitialData, setValue } from './utils/value-utils';
import { useTranslation } from 'react-i18next';

const defaultDebugResult = '';

export const CreateApiDebuggingForm = () => {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const queryClient = useQueryClient();
  const toolId = params?.toolId as string;
  const { formData, resetFormData } = useCreateApiContext();
  const { t } = useTranslation();
  const { mutate: createApiAction, isPending } = useCreateApiAction();
  const { mutateAsync: testApiAction } = useTestApiAction();

  const [debugData, setDebugData] = useState<Record<string, any>>({});
  const [debugResult, setDebugResult] = useState(defaultDebugResult);
  const [debugStatus, setDebugStatus] = useState<'failed' | 'success' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [debuggingType, setDebuggingType] = useState<'run' | 'schema' | null>(null);
  const [inputViewMode, setInputViewMode] = useState<'form' | 'json'>('form');
  const [requestJson, setRequestJson] = useState('');
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (formData.input_parameters && Object.keys(formData.input_parameters).length > 0) {
      setDebugData(
        generateInitialData(formData.input_parameters as unknown as Record<string, IParameter>)
      );
    }
  }, [formData.input_parameters]);

  useEffect(() => {
    if (inputViewMode === 'json') {
      setRequestJson(JSON.stringify(debugData, null, 2));
    }
  }, [debugData, inputViewMode]);

  const handleValueChange = (path: (string | number)[], value: any) => {
    setDebugData((prev) => setValue(prev, path, value));
  };

  const handleViewModeChange = (mode: 'form' | 'json') => {
    if (mode === 'json') {
      setRequestJson(JSON.stringify(debugData, null, 2));
    }
    setInputViewMode(mode);
  };

  const handleJsonChange = (value: string) => {
    setRequestJson(value);
    try {
      const parsed = JSON.parse(value);
      setDebugData(parsed);
    } catch {
      // no-op
    }
  };

  const toggleExpand = (pathStr: string) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [pathStr]: prev[pathStr] === false ? true : false,
    }));
  };

  const handleRunDebug = async (extractionOnly = false) => {
    if (!formData.actionId) {
      showErrorToast({ errors: [{ message: 'Action ID is required to run debug' }] });
      return;
    }

    setDebuggingType(extractionOnly ? 'schema' : 'run');
    setDebugStatus(null);
    setErrorMessage('');

    try {
      const payload = {
        parameters: debugData,
        test_config: {},
        extraction_only: extractionOnly,
        is_open_tool: isBlocksTool,
      };

      const response = await testApiAction({
        toolId,
        actionId: formData.actionId,
        payload,
      });

      const responseData = response as Record<string, any>;
      const resultData = responseData.result || responseData.response_details || response;
      const isSuccess =
        responseData.success !== undefined ? Boolean(responseData.success) : response.is_success;

      if (isSuccess) {
        setDebugStatus('success');
        setDebugResult(JSON.stringify(resultData, null, 2));
        showSuccessToast({ description: t('DEBUG_TEST_COMPLETED') });
      } else {
        setDebugStatus('failed');
        setErrorMessage(response.detail || t('TEST_FAILED'));
        setDebugResult(JSON.stringify(resultData, null, 2));
      }
    } catch (error) {
      setDebugStatus('failed');
      const errorMsg = ErrorTransformer(error);
      const firstError = Array.isArray(errorMsg) ? errorMsg[0] : errorMsg;
      const errorMessage =
        typeof firstError === 'string' ? firstError : firstError?.message || t('UNKNOWN_ERROR');
      setErrorMessage(errorMessage);
      setDebugResult(JSON.stringify({ error: errorMsg }, null, 2));
      showErrorToast({ errors: errorMsg });
    } finally {
      setDebuggingType(null);
    }
  };

  const handleComplete = () => {
    const payload = {
      name: formData.name,
      description: formData.description,
      endpoint: formData.endpoint,
      method: formData.method,
      input_parameters: formData.input_parameters,
      output_parameters: formData.output_parameters,
      is_open_tool: isBlocksTool,
    };

    const apiPayload = formData.actionId
      ? { toolId, actionId: formData.actionId, payload }
      : { toolId, payload };

    createApiAction(apiPayload, {
      onSuccess: (res) => {
        if (res.is_success !== false) {
          showSuccessToast({
            description: formData.actionId
              ? t('API_ACTION_UPDATED_SUCCESSFULLY')
              : t('API_ACTION_CREATED_SUCCESSFULLY'),
          });
          queryClient.invalidateQueries({ queryKey: ['tools', 'details', toolId] });
          resetFormData();
          router.push(`/ai/tool-details/${toolId}/api?isBlocksTool=${isBlocksTool}`);
        } else {
          showErrorToast({
            errors: [{ message: res.message || res.detail || t('FAILED_TO_SAVE_API_ACTION') }],
          });
        }
      },
      onError: (error: unknown) => {
        let errorMessage = t('ERROR_CREATING_API_ACTION');

        if (error && typeof error === 'object' && 'response' in error) {
          const response = (
            error as { response?: { data?: { detail?: string | Array<{ msg: string }> } } }
          ).response;

          if (response?.data?.detail) {
            if (typeof response.data.detail === 'string') {
              errorMessage = response.data.detail;
            } else if (Array.isArray(response.data.detail)) {
              errorMessage = response.data.detail.map((err) => err.msg).join(', ');
            }
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        console.error('Create API action error:', error);
        showErrorToast({ errors: [{ message: errorMessage }] });
      },
    });
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{t('DEBUGGING')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('DEBUGGING_DESCRIPTION')}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col rounded-lg border bg-background" style={{ height: '550px' }}>
          <div className="border-b p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{t('INPUT_PARAMETERS')}</h3>
              <TooltipProvider>
                <div className="inline-flex h-7 items-center rounded-md bg-muted p-0.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleViewModeChange('form')}
                        className={`inline-flex h-6 items-center justify-center rounded px-2 text-xs font-medium transition-all ${
                          inputViewMode === 'form'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <FileText className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('FORM_VIEW')}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleViewModeChange('json')}
                        className={`inline-flex h-6 items-center justify-center rounded px-2 text-xs font-medium transition-all ${
                          inputViewMode === 'json'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Code2 className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('JSON_VIEW')}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>
          </div>

          <div className="flex flex-1 flex-col overflow-hidden p-4">
            {inputViewMode === 'form' ? (
              <div className="flex h-full flex-col">
                <div className="mb-3 grid grid-cols-[1.5fr_1fr_1.5fr] gap-4 text-sm font-medium text-muted-foreground">
                  <div>{t('PARAM_NAME')}</div>
                  <div>{t('TYPE')}</div>
                  <div>{t('VALUE')}</div>
                </div>

                <div className="flex-1 space-y-3 overflow-auto">
                  <div>
                    <DebugParameterList
                      schema={formData.input_parameters as unknown as Record<string, IParameter>}
                      debugData={debugData}
                      expandedPaths={expandedPaths}
                      onToggleExpand={toggleExpand}
                      onValueChange={handleValueChange}
                    />
                    {Object.keys(formData.input_parameters || {}).length === 0 && (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        {t('NO_INPUT_PARAMS_DEFINED')}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRunDebug(false)}
                    disabled={debuggingType !== null}
                  >
                    <Play className="mr-2 inline h-4 w-4" />
                    {debuggingType === 'run' ? t('RUNNING') : t('RUN_DEBUG')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRunDebug(true)}
                    disabled={debuggingType !== null}
                  >
                    <Play className="mr-2 inline h-4 w-4" />
                    {debuggingType === 'schema' ? t('RUNNING') : t('DEBUG_SCHEMA')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col">
                <div className="flex-1 overflow-hidden rounded-lg bg-muted/50 p-3">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">{t('REQUEST')}</p>
                  <textarea
                    className="h-[400px] w-full resize-none rounded bg-background p-3 font-mono text-sm outline-none"
                    value={requestJson}
                    onChange={(event) => handleJsonChange(event.target.value)}
                    spellCheck={false}
                  />
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRunDebug(false)}
                    disabled={debuggingType !== null}
                  >
                    <Play className="mr-2 inline h-4 w-4" />
                    {debuggingType === 'run' ? t('RUNNING') : t('RUN_DEBUG')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRunDebug(true)}
                    disabled={debuggingType !== null}
                  >
                    <Play className="mr-2 inline h-4 w-4" />
                    {debuggingType === 'schema' ? t('RUNNING') : t('DEBUG_SCHEMA')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col rounded-lg border bg-background" style={{ height: '550px' }}>
          <div className="border-b p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{t('DEBUG_RESULT')}</h3>
              {debugStatus && (
                <Badge
                  variant={debugStatus === 'failed' ? 'destructive' : 'default'}
                  className="rounded-sm"
                >
                  {debugStatus === 'failed' ? t('FAILED') : t('SUCCESS')}
                </Badge>
              )}
            </div>
          </div>

          <div className="flex flex-1 flex-col overflow-hidden p-4">
            <div className="flex-1 overflow-hidden rounded-lg bg-muted/50 p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">{t('RESPONSE')}</p>
              <textarea
                className="h-[400px] w-full resize-none rounded bg-background p-3 font-mono text-sm outline-none"
                value={debugResult}
                readOnly
                spellCheck={false}
              />
            </div>
            {debugStatus === 'failed' && errorMessage && (
              <div className="mt-2 rounded-md bg-destructive/10 p-2">
                <p className="text-xs text-error">{errorMessage}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-start">
        <Button onClick={handleComplete} disabled={isPending} className="px-8">
          {isPending ? t('CREATING') : t('COMPLETE')}
        </Button>
      </div>
    </div>
  );
};
